/**
 * PaperlessPanel
 *
 * Kompakte Dokumentenliste mit Suchfunktion, Thumbnail-Vorschau,
 * Upload-Funktion und Link zur Paperless-Instanz.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  fetchDocuments,
  fetchTags,
  fetchDocumentTypes,
  fetchCustomFieldDefs,
  uploadDocument,
  updateDocument,
  fetchTaskStatus,
  getDocumentThumbnailUrl,
  getDocumentDownloadUrl,
  getDocumentPreviewUrl,
  getDocumentWebUrl,
  checkConnection,
  PAPERLESS_BASE_URL,
  type PaperlessDocument,
  type PaperlessTag,
  type PaperlessDocumentType,
  type PaperlessCustomFieldDef,
  type PaperlessListResponse,
} from '../../lib/paperless/api'

// ── Icons ──────────────────────────────────────────────────────────────

function IconSearch() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  )
}
function IconUpload() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
    </svg>
  )
}
function _SparkleIconUnused() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ flexShrink: 0 }}>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
      <path d="M20 3v4"/><path d="M22 5h-4"/>
      <path d="M4 17v2"/><path d="M5 18H3"/>
    </svg>
  )
}
function IconExternalLink() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  )
}
function IconDownload() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  )
}
function IconFile() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  )
}
function IconRefresh() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  )
}
function IconX() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="4" y1="4" x2="20" y2="20"/><line x1="20" y1="4" x2="4" y2="20"/>
    </svg>
  )
}
function IconCheck() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    })
  } catch { return iso }
}

function truncate(str: string, max: number): string {
  return str.length > max ? str.substring(0, max) + '…' : str
}

// ── Component ──────────────────────────────────────────────────────────

interface PaperlessPanelProps {
  /** Called when a document is selected for preview in the main area */
  onSelectDoc?: (docId: string | null, summary?: string | null) => void
  /** Currently selected doc ID (for highlighting) */
  selectedDocId?: string | null
  /** Active project ID — used to filter documents by project's Paperless tag */
  projectId?: string | null
}

export function PaperlessPanel({ onSelectDoc, selectedDocId, projectId }: PaperlessPanelProps = {}) {
  const [documents, setDocuments] = useState<PaperlessDocument[]>([])
  const [tags, setTags] = useState<PaperlessTag[]>([])
  const [docTypes, setDocTypes] = useState<PaperlessDocumentType[]>([])
  const [customFieldDefs, setCustomFieldDefs] = useState<PaperlessCustomFieldDef[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [connected, setConnected] = useState<boolean | null>(null)
  const [query, setQuery] = useState('')
  const [searchActive, setSearchActive] = useState(false)

  // Filter
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [selectedDocTypeId, setSelectedDocTypeId] = useState<number | null>(null)

  // Project-spezifischer Paperless-Tag (aus Backend geladen)
  const [projectTagId, setProjectTagId] = useState<number | null>(null)

  // Upload
  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [previewDocId, setPreviewDocId] = useState<number | null>(null)

  const PAGE_SIZE = 20

  // Ref für projectTagId — damit loadDocuments immer den aktuellen Wert sieht
  const projectTagIdRef = useRef<number | null>(null)

  // Field-ID des Custom Fields "paperless_tag_id" (wird beim Laden der Defs aufgelöst)
  const _paperlessTagFieldIdRef = useRef<number | null>(null)
  // Shortcut für loadDocuments Closure
  let _paperlessTagFieldId: number | null = _paperlessTagFieldIdRef.current
  projectTagIdRef.current = projectTagId

  // Project-Tag aus window.currentProject lesen (wird von script.js gesetzt)
  useEffect(() => {
    if (!projectId) {
      setProjectTagId(null)
      return
    }

    function readTagFromWindow(): number | null {
      const proj = (window as any).currentProject
      if (proj?.id === projectId && proj.paperlessTagId != null) {
        return Number(proj.paperlessTagId)
      }
      return null
    }

    // Sofort lesen
    const tagId = readTagFromWindow()
    console.log('[PaperlessPanel] projectTagId from window:', tagId, 'project:', projectId)
    setProjectTagId(tagId)

    // Falls window.currentProject sich verzögert ändert, per Interval nachpolling
    const interval = setInterval(() => {
      const newTagId = readTagFromWindow()
      setProjectTagId(prev => prev === newTagId ? prev : newTagId)
    }, 1000)

    return () => clearInterval(interval)
  }, [projectId])

  /**
   * Baut die effektiven Tag-IDs: User-Filter + Projekt-Tag.
   * Nutzt den Ref, damit immer der aktuelle projectTagId verwendet wird.
   */
  function buildEffectiveTagIds(userTags: number[]): number[] | undefined {
    const ptId = projectTagIdRef.current
    if (ptId == null) return userTags.length > 0 ? userTags : undefined
    const combined = new Set([...userTags, ptId])
    return [...combined]
  }

  // Connection prüfen + Metadaten laden (OHNE Dokumente — die kommen im nächsten Effect)
  useEffect(() => {
    checkConnection().then(ok => {
      setConnected(ok)
      if (ok) {
        loadTags()
        loadDocTypes()
        loadCustomFieldDefs()
      }
    })
  }, [])

  // Dokumente laden wenn: connected UND projectTagId aufgelöst ist
  // Feuert bei: erstem connected=true, projectTagId-Änderung, projectId-Wechsel
  const initialLoadDone = useRef(false)
  useEffect(() => {
    if (connected !== true) return

    const tags = buildEffectiveTagIds(selectedTagIds)
    console.log('[PaperlessPanel] Loading docs — projectTagId:', projectTagId, 'effectiveTags:', tags)
    loadDocuments(1, query, tags, selectedDocTypeId)
    initialLoadDone.current = true
  }, [projectTagId, connected])

  // ── Dokumente laden (mit Filter-Support) ──────────────────────────
  // paperless_tag_id ist ein Custom Field in Paperless, kein Tag.
  // Der Proxy leitet Filter-Params nicht weiter → client-seitige Filterung.
  const loadDocuments = useCallback(async (
    p: number,
    q?: string,
    _tagIds?: number[],
    docTypeId?: number | null,
  ) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchDocuments({
        page: p,
        page_size: PAGE_SIZE,
        ordering: '-created',
        query: q || undefined,
        document_type__id: docTypeId ?? undefined,
      })

      setDocuments(res.results)
      setTotalCount(res.count)
      setPage(p)
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadTags = useCallback(async () => {
    try {
      const res = await fetchTags()
      setTags(res.results)
    } catch { /* silent */ }
  }, [])

  const loadDocTypes = useCallback(async () => {
    try {
      const res = await fetchDocumentTypes()
      setDocTypes(res.results)
    } catch { /* silent */ }
  }, [])

  const loadCustomFieldDefs = useCallback(async () => {
    try {
      const res = await fetchCustomFieldDefs()
      setCustomFieldDefs(res.results)
      // Field-ID für "paperless_tag_id" merken
      const ptField = res.results.find(cf => cf.name === 'paperless_tag_id')
      if (ptField) {
        _paperlessTagFieldIdRef.current = ptField.id
        _paperlessTagFieldId = ptField.id
        console.log('[PaperlessPanel] Custom field "paperless_tag_id" has ID:', ptField.id)
      }
    } catch {
      // Fallback: bekannte Field-IDs der Paperless-Instanz
      setCustomFieldDefs([
        { id: 3, name: 'Summary', data_type: 'string' },
        { id: 4, name: 'doc_title', data_type: 'string' },
      ])
    }
  }, [])

  // ── Suche + Filter ─────────────────────────────────────────────────
  const reloadWithFilters = useCallback((
    q?: string, tagIds?: number[], dtId?: number | null,
  ) => {
    const userTags = tagIds ?? selectedTagIds
    loadDocuments(1, q ?? query, buildEffectiveTagIds(userTags), dtId !== undefined ? dtId : selectedDocTypeId)
  }, [query, selectedTagIds, selectedDocTypeId, loadDocuments])

  const handleSearch = useCallback(() => {
    reloadWithFilters()
  }, [reloadWithFilters])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
    if (e.key === 'Escape') {
      setQuery('')
      setSearchActive(false)
      reloadWithFilters('', selectedTagIds, selectedDocTypeId)
    }
  }, [handleSearch, reloadWithFilters, selectedTagIds, selectedDocTypeId])

  const toggleTagFilter = useCallback((tagId: number) => {
    setSelectedTagIds(prev => {
      const next = prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
      reloadWithFilters(query, next, selectedDocTypeId)
      return next
    })
  }, [query, selectedDocTypeId, reloadWithFilters])

  const toggleDocTypeFilter = useCallback((dtId: number) => {
    setSelectedDocTypeId(prev => {
      const next = prev === dtId ? null : dtId
      reloadWithFilters(query, selectedTagIds, next)
      return next
    })
  }, [query, selectedTagIds, reloadWithFilters])

  const handleUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    setUploadSuccess(false)
    setError(null)
    try {
      for (const file of Array.from(files)) {
        const result = await uploadDocument(file, {
          title: file.name.replace(/\.[^.]+$/, ''),
        })

        // In Prod: task_id polling bis Paperless fertig ist
        let uploadedDocId: number | null = null
        if (typeof result === 'object' && result.task_id) {
          const maxAttempts = 30
          for (let i = 0; i < maxAttempts; i++) {
            await new Promise(r => setTimeout(r, 2000))
            try {
              const status = await fetchTaskStatus(result.task_id)
              if (status.status === 'SUCCESS') {
                uploadedDocId = status.document_id ?? null
                break
              }
              if (status.status === 'FAILURE') {
                throw new Error('Paperless-Verarbeitung fehlgeschlagen')
              }
            } catch {
              // Task-Endpoint nicht verfügbar — Fallback: einfach weiter warten
            }
          }
        }

        // Custom Field "paperless_tag_id" auf dem neuen Dokument setzen
        const ptId = projectTagIdRef.current
        const fieldId = _paperlessTagFieldIdRef.current
        if (uploadedDocId && ptId != null && fieldId != null) {
          try {
            await updateDocument(uploadedDocId, {
              custom_fields: [{ field: fieldId, value: ptId }],
            })
            console.log('[PaperlessPanel] Custom field paperless_tag_id=' + ptId, 'set on doc', uploadedDocId)
          } catch (err) {
            console.warn('[PaperlessPanel] Custom field setzen fehlgeschlagen:', err)
          }
        }
      }
      setUploadSuccess(true)
      setTimeout(() => setUploadSuccess(false), 3000)
      loadDocuments(1, query, buildEffectiveTagIds(selectedTagIds), selectedDocTypeId)
    } catch (err: any) {
      setError(err.message || 'Upload fehlgeschlagen')
    } finally {
      setUploading(false)
    }
  }, [loadDocuments, query, selectedTagIds, selectedDocTypeId])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleUpload(e.dataTransfer.files)
  }, [handleUpload])

  const tagMap = new Map(tags.map(t => [t.id, t]))
  const docTypeMap = new Map(docTypes.map(dt => [dt.id, dt]))
  const cfNameMap = new Map(customFieldDefs.map(cf => [cf.id, cf.name]))

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const hasFilters = selectedTagIds.length > 0 || selectedDocTypeId !== null

  // ── Disconnected State ──────────────────────────────────────────────
  if (connected === false) {
    return (
      <div className="ghost-empty">
        <div style={{ fontSize: '1.5em', marginBottom: '0.75em', opacity: 0.4 }}>
          <IconFile />
        </div>
        <div>
          Paperless nicht erreichbar
        </div>
        <div className="ghost-empty__hint">
          Verbindung zu {PAPERLESS_BASE_URL} fehlgeschlagen.
        </div>
        <button
          onClick={() => { setConnected(null); checkConnection().then(ok => { setConnected(ok); if (ok) loadDocuments(1) }) }}
          className="ghost-chip ghost-chip--active"
          style={{ marginTop: '0.75em' }}
        >
          <IconRefresh /> Erneut versuchen
        </button>
      </div>
    )
  }

  return (
    <div
      data-component="PaperlessPanel"
      className="ghost-panel"
      onDragOver={e => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* ── Drag-Overlay ─────────────────────────────────────── */}
      {dragOver && (
        <div className={`ghost-dropzone ${dragOver ? 'ghost-dropzone--active' : ''}`} style={{
          position: 'absolute', inset: 0, zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: '0.5em',
        }}>
          <IconUpload />
          <span style={{ fontSize: '0.75em', fontWeight: 600 }}>
            Datei hier ablegen
          </span>
        </div>
      )}

      {/* ── Header: Suche + Upload ───────────────────────────── */}
      <div className="ghost-search">
        {/* Suche */}
        <div className="ghost-search__inner">
          <span className="ghost-search__icon"><IconSearch /></span>
          <input
            className="ghost-search__input"
            value={query}
            onChange={e => {
              const val = e.target.value
              setQuery(val)
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
              searchDebounceRef.current = setTimeout(() => {
                loadDocuments(1, val, buildEffectiveTagIds(selectedTagIds), selectedDocTypeId)
              }, 350)
            }}
            onFocus={() => setSearchActive(true)}
            onBlur={() => !query && setSearchActive(false)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Suchen …"
          />
          {query && (
            <button
              className="ghost-search__clear"
              onClick={() => { setQuery(''); loadDocuments(1, '', buildEffectiveTagIds(selectedTagIds), selectedDocTypeId) }}
            >
              <IconX />
            </button>
          )}
        </div>

        {/* Upload-Button + Refresh + Paperless Link */}
        <div className="ghost-search__actions">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-tooltip="Hochladen"
            className={`ghost-search__btn${uploadSuccess ? ' ghost-search__btn--success' : ''}`}
            style={{ opacity: uploading ? 0.5 : 1 }}
          >
            {uploadSuccess ? <IconCheck /> : uploading ? '…' : <IconUpload />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.tiff,.txt,.docx,.xlsx"
            onChange={e => handleUpload(e.target.files)}
            style={{ display: 'none' }}
          />

          <button
            onClick={() => loadDocuments(page, query, buildEffectiveTagIds(selectedTagIds), selectedDocTypeId)}
            data-tooltip="Aktualisieren"
            className="ghost-search__btn"
          >
            <IconRefresh />
          </button>

          <a
            href={PAPERLESS_BASE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-tooltip="Paperless öffnen"
            className="ghost-search__btn"
          >
            <IconExternalLink />
          </a>

          <button
            onClick={() => (window as any).dinChatOpen?.()}
            data-tooltip="Agent zu Docs befragen"
            className="ghost-search__btn ghost-search__btn--agent"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/>
              <path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Error ────────────────────────────────────────────── */}
      {error && (
        <div style={{
          padding: '6px 16px',
          fontSize: '10px',
          background: '#fee2e2',
          color: '#dc2626',
          borderBottom: '1px solid #fecaca',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} className="ghost-action-btn"><IconX /></button>
        </div>
      )}

      {/* ── Filter: Document Types + Tags ──────────────────────── */}
      {(docTypes.length > 0 || tags.length > 0) && (
        <div className="ghost-filters">
          {/* Document Types als Chips */}
          {docTypes.length > 0 && (
            <div className="ghost-filters__row">
              {docTypes.map(dt => {
                const active = selectedDocTypeId === dt.id
                return (
                  <button
                    key={dt.id}
                    onClick={() => toggleDocTypeFilter(dt.id)}
                    className={`ghost-chip ${active ? 'ghost-chip--active' : ''}`}
                  >
                    {dt.name}
                  </button>
                )
              })}
            </div>
          )}

          {/* Tags als Chips */}
          {tags.length > 0 && (
            <div className="ghost-filters__row">
              {tags.map(tag => {
                const active = selectedTagIds.includes(tag.id)
                const tagColor = tag.color || '#9ca3af'
                return (
                  <button
                    key={tag.id}
                    onClick={() => toggleTagFilter(tag.id)}
                    className={`ghost-chip ${active ? 'ghost-chip--active' : ''}`}
                    style={{
                      borderColor: active ? tagColor + 'aa' : tagColor + '33',
                      background: active ? tagColor + '22' : 'transparent',
                      color: active ? tagColor : '#6b7280',
                    }}
                  >
                    {tag.name}
                  </button>
                )
              })}
            </div>
          )}

          {/* Active filter clear */}
          {hasFilters && (
            <button
              onClick={() => {
                setSelectedTagIds([])
                setSelectedDocTypeId(null)
                reloadWithFilters(query, [], null)
              }}
              className="ghost-filters__reset"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}

      {/* ── Dokument-Liste ───────────────────────────────────── */}
      <div className="ghost-list">
        {loading && documents.length === 0 ? (
          <div className="ghost-empty">
            Dokumente laden …
          </div>
        ) : documents.length === 0 ? (
          <div className="ghost-empty">
            Keine Dokumente gefunden
          </div>
        ) : (
          documents.map(doc => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              tagMap={tagMap}
              docTypeMap={docTypeMap}
              cfNameMap={cfNameMap}
              isPreview={previewDocId === doc.id || selectedDocId === String(doc.id)}
              onOpenPdf={() => {
                setPreviewDocId(doc.id)
                // AppShell-Integration: Doc-Vorschau im MainArea anzeigen
                if (onSelectDoc) {
                  onSelectDoc(
                    String(doc.id),
                    doc.custom_fields?.find(f => cfNameMap.get(f.field) === 'Summary')?.value || null,
                  )
                  return
                }
                // Fallback: Globalen Dokument-Viewer öffnen
                if ((window as any).showDocViewer) {
                  ;(window as any).showDocViewer({
                    id: doc.id,
                    title: doc.title,
                    fileName: doc.original_file_name || '',
                    tags: doc.tags || [],
                    created: doc.created,
                    pageCount: (doc as any).page_count || null,
                    docTypeName: doc.document_type ? (docTypeMap.get(doc.document_type) || null) : null,
                    summary: doc.custom_fields?.find(f => cfNameMap.get(f.field) === 'Summary')?.value || null,
                  })
                } else {
                  import('../../lib/paperless/api').then(({ getDocumentPreviewUrl }) => {
                    window.open(getDocumentPreviewUrl(doc.id), '_blank')
                  })
                }
              }}
            />
          ))
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="ghost-footer" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span>{totalCount} Dokument{totalCount !== 1 ? 'e' : ''}</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => loadDocuments(page - 1, query, buildEffectiveTagIds(selectedTagIds), selectedDocTypeId)}
              disabled={page <= 1}
              className="ghost-action-btn"
              style={{ opacity: page <= 1 ? 0.3 : 1 }}
            >
              ‹
            </button>
            <span style={{ padding: '0 10px', fontSize: '13px', lineHeight: 1.3 }}>
              {page}/{totalPages}
            </span>
            <button
              onClick={() => loadDocuments(page + 1, query, buildEffectiveTagIds(selectedTagIds), selectedDocTypeId)}
              disabled={page >= totalPages}
              className="ghost-action-btn"
              style={{ opacity: page >= totalPages ? 0.3 : 1 }}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Sub: Dokumentzeile ────────────────────────────────────────────────

function DocumentRow({
  doc,
  tagMap,
  docTypeMap,
  cfNameMap,
  isPreview,
  onOpenPdf,
}: {
  doc: PaperlessDocument
  tagMap: Map<number, PaperlessTag>
  docTypeMap: Map<number, PaperlessDocumentType>
  cfNameMap: Map<number, string>
  isPreview: boolean
  onOpenPdf: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const [thumbError, setThumbError] = useState(false)
  const [showSummary, setShowSummary] = useState(false)
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)

  // Note (localStorage, live via custom event from DocReader)
  const [docNote, setDocNote] = useState(() => {
    try { return localStorage.getItem(`paperless_note_${doc.id}`) || '' } catch { return '' }
  })
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ docId: string | number; note: string }>
      if (String(ev.detail.docId) === String(doc.id)) setDocNote(ev.detail.note)
    }
    window.addEventListener('paperless-note-changed', handler)
    return () => window.removeEventListener('paperless-note-changed', handler)
  }, [doc.id])

  // Custom Fields per Name-Lookup auflösen
  const getCustomField = (name: string) =>
    doc.custom_fields?.find(f => cfNameMap.get(f.field) === name)?.value || null
  const docTitle = getCustomField('doc_title')
  const summary = getCustomField('Summary')
  const displayTitle = docTitle || doc.title

  const handleMouseEnter = () => {
    setHovered(true)
    if (summary) {
      hoverTimerRef.current = setTimeout(() => {
        if (rowRef.current) {
          const rect = rowRef.current.getBoundingClientRect()
          setPopupPos({ top: rect.top, left: rect.right + 8 })
        }
        setShowSummary(true)
      }, 400)
    }
  }

  const handleMouseLeave = () => {
    setHovered(false)
    setShowSummary(false)
    setPopupPos(null)
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  return (
    <div
      ref={rowRef}
      data-element="paperless-doc-row"
      data-doc-id={doc.id}
      draggable
      onDragStart={e => {
        e.dataTransfer.setData('application/x-paperless-doc', JSON.stringify({
          id: doc.id,
          title: displayTitle,
          fileName: doc.original_file_name,
          downloadUrl: `https://ortofapi.administer.cloud/paperless/documents/documents/${doc.id}/download`,
        }))
        e.dataTransfer.effectAllowed = 'copy'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`ghost-item ${isPreview ? 'ghost-item--active' : ''}`}
      onClick={onOpenPdf}
    >
      {/* Thumbnail */}
      <div className="ghost-thumbnail">
        {thumbError ? (
          <span style={{ color: '#9ca3af', opacity: 0.4 }}><IconFile /></span>
        ) : (
          <img
            src={getDocumentThumbnailUrl(doc.id)}
            alt=""
            onError={() => setThumbError(true)}
          />
        )}
      </div>

      {/* Content */}
      <div className="ghost-item__content">
        <div className="ghost-item__title">
          {displayTitle}
        </div>

        <div className="ghost-item__meta">
          <span className="ghost-item__meta-date">{formatDate(doc.created)}</span>
          {doc.original_file_name && (
            <span className="ghost-item__meta-filename">
              {truncate(doc.original_file_name, 20)}
            </span>
          )}
        </div>

        {/* Note preview */}
        {docNote && (
          <div className="ghost-item__note">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
              <path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/>
            </svg>
            {docNote.length > 40 ? docNote.slice(0, 40) + '…' : docNote}
          </div>
        )}

        {/* Tags */}
        {doc.tags.length > 0 && (
          <div className="ghost-tags">
            {doc.tags.slice(0, 4).map(tid => {
              const tag = tagMap.get(tid)
              return (
                <span key={tid} className="ghost-tag" style={{
                  background: tag?.color ? tag.color + '22' : '#f3f4f6',
                  color: tag?.color || '#9ca3af',
                  borderColor: tag?.color ? tag.color + '33' : '#e5e7eb',
                  border: `1px solid ${tag?.color ? tag.color + '33' : '#e5e7eb'}`,
                }}>
                  {tag?.name ?? `#${tid}`}
                </span>
              )
            })}
            {doc.tags.length > 4 && (
              <span className="ghost-tag">
                +{doc.tags.length - 4}
              </span>
            )}
          </div>
        )}
      </div>

      {doc.document_type && docTypeMap.get(doc.document_type) && (
        <div className="ghost-item__aside">
          <span className="ghost-item__badge">
            {docTypeMap.get(doc.document_type)!.name}
          </span>
        </div>
      )}

      {/* Aktions-Buttons (bei Hover) */}
      <div className="ghost-actions">
        <a
          href={`https://ortofapi.administer.cloud/paperless/documents/documents/${doc.id}/download`}
          target="_blank"
          rel="noopener noreferrer"
          title="Herunterladen"
          onClick={e => e.stopPropagation()}
          className="ghost-action-btn"
          style={{ textDecoration: 'none' }}
        >
          <IconDownload />
        </a>
        <a
          href={getDocumentWebUrl(doc.id)}
          target="_blank"
          rel="noopener noreferrer"
          title="In Paperless öffnen"
          onClick={e => e.stopPropagation()}
          className="ghost-action-btn"
          style={{ textDecoration: 'none' }}
        >
          <IconExternalLink />
        </a>
      </div>

      {/* Summary-Popup (Portal) */}
      {showSummary && summary && popupPos && createPortal(
        <div
          className="doc-viewer-summary doc-summary-popup"
          style={{ top: popupPos.top, left: popupPos.left }}
        >
          <div className="doc-viewer-summary__handle">
            <svg
              className="dok-card-ai-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
              <path d="M20 3v4"/><path d="M22 5h-4"/>
            </svg>
            <span className="doc-viewer-summary__label">Zusammenfassung</span>
          </div>
          <div className="doc-viewer-summary__body">
            <div className="doc-viewer-summary__skeleton" aria-hidden="true" />
            <p>{summary}</p>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
